import {
    faBellConcierge,
    faBookUser,
    faBullhorn,
    faCalendarDays,
    faChartLineUp,
    faCreditCard,
    faGear,
    faList,
    faQuestionCircle,
    faTag,
    faUsers
} from '@fortawesome/pro-light-svg-icons';
import { Categories as InsightChildren, getCategoryKey } from 'components/Insights/Data/InsightStructure';
import { MenuLink } from 'model/Menu';
import { useSelector } from 'react-redux';
import { MenuState } from 'reducers/menu/MenuState';
import {
    AgreementsPath,
    BookingsPath,
    BookingsRootPath,
    BusinessSettingsPath,
    CalendarPath,
    ClientsPath,
    DaycarePath,
    TransportationPath,
    HelpSupportPath,
    InsightsPath,
    MarketingPath,
    MembershipsPath,
    PaymentsPath,
    ProductsPath,
    ServicesPath,
    StaffPath,
    MarketingPromoCodes,
    SoldMembershipsPath,
    MarketingReviewsPath,
    FormsPath,
    VaccineApprovalsPath,
    BoardingPath,
    DiscountsPath,
    MarketingLeadsPath,
    MarketingLoyaltyPath,
    MarketingEmailsPath
} from 'routes/Paths';
import store, { RootState } from 'store';
import { isMarketingEnabled, isPaymentsEnabled } from 'utils/MarketingUtils';
import { membershipMarketplaceIds } from 'utils/MarketplaceConfig';
import { isDiscountsEnabled } from 'utils/DiscountsUtils';
import { SETTINGS_TRUE_VALUE } from '@spike/marketplace-model';
import usePermissions, { Permissions } from './usePermissions';
import { canSetupEvaluation } from './useServiceCategoryAccess';
import { MANAGER_ID, OWNER_ID } from '@spike/model';
import { isSettingEnabled } from 'utils/SettingsUtils';
const MARKETPLACE_IDS_FOR_MEMBERSHIP: number[] = membershipMarketplaceIds();

const menuLinks: Array<MenuLink> = [
    {
        id: 'bookings',
        label: 'Bookings',
        icon: faBellConcierge,
        path: BookingsRootPath,
        activePath: BookingsPath
    },
    {
        id: 'calendar',
        label: 'Calendar',
        icon: faCalendarDays,
        path: CalendarPath
    },
    {
        id: 'clients',
        label: 'Clients',
        icon: faBookUser,
        path: ClientsPath,
        childs: [
            {
                id: 'clients-list',
                label: 'Clients List',
                path: ClientsPath
            },
            {
                id: 'agreements',
                label: 'Agreements',
                path: AgreementsPath
            },
            {
                id: 'sold-memberships',
                label: 'Sold Memberships',
                path: SoldMembershipsPath,
                exclusiveRoleIds: [MANAGER_ID, OWNER_ID]
            },
            {
                id: 'forms',
                label: 'Forms',
                path: FormsPath
            },
            {
                id: 'vaccine-approvals',
                label: 'Vaccine Approvals',
                path: VaccineApprovalsPath
            }
        ]
    },
    {
        id: 'staff',
        label: 'Staff',
        icon: faUsers,
        path: StaffPath
    },
    {
        id: 'services',
        label: 'Services',
        icon: faList,
        path: ServicesPath,
        childs: [
            {
                id: 'primary-services',
                label: 'Grooming',
                path: ServicesPath
            },
            {
                id: 'daycare',
                label: 'Daycare',
                path: DaycarePath
            },
            {
                id: 'boarding',
                label: 'Boarding',
                path: BoardingPath
            },
            {
                id: 'transportation',
                label: 'Transportation',
                path: TransportationPath
            },
            {
                id: 'addons',
                label: 'Add-ons',
                path: `${ServicesPath}#add_ons`
            },
            {
                id: 'memberships',
                label: 'Memberships & Packages',
                path: MembershipsPath,
                exclusiveRoleIds: [MANAGER_ID, OWNER_ID],
                exclusiveMarketplaceIds: MARKETPLACE_IDS_FOR_MEMBERSHIP
            },
            {
                id: 'evaluation',
                label: 'Evaluation',
                path: `${ServicesPath}#evaluation`,
                exclusiveRoleIds: [MANAGER_ID, OWNER_ID]
            },
            {
                id: 'discounts',
                label: 'Automatic Discounts',
                path: DiscountsPath,
                exclusiveRoleIds: [MANAGER_ID, OWNER_ID],
                exclusiveMarketplaceIds: MARKETPLACE_IDS_FOR_MEMBERSHIP
            }
        ]
    },
    {
        id: 'products',
        label: 'Inventory',
        icon: faTag,
        path: ProductsPath,
        childs: [
            {
                id: 'inventory-dashboard',
                label: 'Dashboard',
                path: `${ProductsPath}#dashboard`
            },
            {
                id: 'inventory-products',
                label: 'Products',
                path: `${ProductsPath}#products`
            },
            {
                id: 'inventory-stock',
                label: 'Stock',
                path: `${ProductsPath}#stock`
            },
            {
                id: 'inventory-suppliers',
                label: 'Suppliers',
                path: `${ProductsPath}#vendors`
            },
            {
                id: 'inventory-categories',
                label: 'Categories',
                path: `${ProductsPath}#categories`
            }
        ]
    },
    {
        id: 'marketing',
        label: 'Marketing',
        icon: faBullhorn,
        path: MarketingPath,
        childs: [
            {
                id: 'sms-campaigns',
                label: 'SMS Campaigns',
                path: MarketingPath
            },
            ...(process.env.REACT_APP_LEADS_ENABLED === 'true'
                ? [
                      {
                          id: 'leads',
                          label: 'Leads',
                          path: MarketingLeadsPath
                      }
                  ]
                : []),
            {
                id: 'reviews',
                label: 'Reviews',
                path: MarketingReviewsPath
            },
            {
                id: 'loyalty',
                label: 'Loyalty',
                path: MarketingLoyaltyPath
            },
            {
                id: 'promo-codes',
                label: 'Promo Codes',
                path: MarketingPromoCodes
            },
            {
                id: 'marketing-emails',
                label: 'Emails',
                path: MarketingEmailsPath
            }
        ]
    },
    {
        id: 'insights',
        label: 'Insights',
        icon: faChartLineUp,
        path: InsightsPath,
        childs: [
            {
                id: 'insights-dashboard',
                label: 'Dashboard',
                path: `${InsightsPath}#${getCategoryKey(InsightChildren.DASHBOARD)}`
            },
            {
                id: 'insights-bookings',
                label: 'Bookings',
                path: `${InsightsPath}#${getCategoryKey(InsightChildren.BOOKINGS)}`
            },
            {
                id: 'insights-sales',
                label: 'Sales',
                path: `${InsightsPath}#${getCategoryKey(InsightChildren.SALES)}`
            },
            {
                id: 'insights-clients-pets',
                label: 'Clients & Pets',
                path: `${InsightsPath}#${getCategoryKey(InsightChildren.CLIENTS_AND_PETS)}`
            },
            {
                id: 'insights-staff',
                label: 'Staff',
                path: `${InsightsPath}#${getCategoryKey(InsightChildren.STAFF)}`
            }
        ]
    },
    {
        id: 'payments',
        label: 'Payments',
        icon: faCreditCard,
        path: PaymentsPath,
        childs: [
            {
                id: 'dashboard',
                label: 'Dashboard',
                path: `${PaymentsPath}#DASHBOARD`
            },
            {
                id: 'new-sale',
                label: 'New Sale',
                path: `${PaymentsPath}#SINGLE_SALE`
            },
            {
                id: 'sales',
                label: 'Single Sales',
                path: `${PaymentsPath}#SALES`
            },
            {
                id: 'transactions',
                label: 'Transactions',
                path: `${PaymentsPath}#TRANSACTIONS`
            },
            {
                id: 'sales-reports',
                label: 'Sales Reports',
                path: `${PaymentsPath}#SALES_REPORTS`
            },
            {
                id: 'tips-details',
                label: 'Tips Details',
                path: `${PaymentsPath}#TIPS_DETAIL`
            },
            {
                id: 'commissions-details',
                label: 'Commissions',
                path: `${PaymentsPath}#COMMISSIONS`
            },
            {
                id: 'bank-accounts',
                label: 'Bank Accounts',
                path: `${PaymentsPath}#BANK_ACCOUNTS`
            },
            {
                id: 'terminals',
                label: 'Terminals',
                path: `${PaymentsPath}#TERMINALS`
            },
            {
                id: 'payouts',
                label: 'Payouts',
                path: `${PaymentsPath}#TRANSFERS`
            },
            {
                id: 'setup',
                label: 'Setup',
                path: `${PaymentsPath}#SETUP`
            }
        ]
    },
    {
        id: 'settings',
        label: 'Business Settings',
        icon: faGear,
        path: BusinessSettingsPath
    },
    {
        id: 'helpCenter',
        label: 'Help & Support',
        icon: faQuestionCircle,
        path: HelpSupportPath
    }
];

export const useMenu = () => {
    const menu = useSelector<RootState, MenuState>(state => state.menu);

    return menu;
};

export const useSidebarLinks = (): Array<MenuLink> => {
    const permissions = usePermissions();
    return filterEnabledLinks(menuLinks, permissions).filter(link => !['settings', 'helpCenter'].includes(link.id));
};

export const useSidebarFooterLinks = (): Array<MenuLink> => {
    const permissions = usePermissions();
    return filterEnabledLinks(menuLinks, permissions).filter(link => ['settings', 'helpCenter'].includes(link.id));
};

export const useMenuLinks = (): Array<MenuLink> => {
    const permissions = usePermissions();
    return filterEnabledLinks(menuLinks, permissions);
};

const filterEnabledLinks = (links: Array<MenuLink>, permissions: Permissions): Array<MenuLink> => {
    const user = store.getState().login.auth.user;
    const marketplace = store.getState().marketplace.marketplace;

    const paymentsEnabled = isPaymentsEnabled(marketplace) && permissions.checkout.manage;
    const marketingEnabled = isMarketingEnabled(marketplace.id) && permissions.marketing;
    const discountsEnabled = isDiscountsEnabled(marketplace.id);
    const insightsEnabled = permissions.insights;
    const daycareEnabled = isSettingEnabled(marketplace.settings, 'daycare_enabled');
    const boardingEnabled = isSettingEnabled(marketplace.settings, 'boarding_enabled');
    const transportationEnabled = isSettingEnabled(marketplace.settings, 'transportation_enabled');
    const groomingEnabled = isSettingEnabled(marketplace.settings, 'grooming_enabled');
    // Keep this in sync with the Services page sidebar, which uses the same predicate
    const evaluationEnabled = canSetupEvaluation({
        hasEvaluation: marketplace.evaluationEnabled,
        hasGrooming: groomingEnabled,
        hasDaycare: daycareEnabled,
        hasBoarding: boardingEnabled
    });
    const servicesEnabled = permissions.services;
    const productsEnabled = permissions.products;

    const bookingsEnabled = permissions.bookings;
    const calendarEnabled = permissions.calendar;
    const settingsEnabled = permissions.settings;
    const helpEnabled = permissions.help;
    const clientsEnabled = permissions.clients.manage;
    const staffEnabled = permissions.staff;
    const membershipsEnabled = permissions.memberships.manage;

    return links
        .filter(item => item.id !== 'bookings' || bookingsEnabled)
        .filter(item => item.id !== 'calendar' || calendarEnabled)
        .filter(item => item.id !== 'settings' || settingsEnabled)
        .filter(item => item.id !== 'helpCenter' || helpEnabled)
        .filter(item => item.id !== 'clients' || clientsEnabled)
        .filter(item => item.id !== 'staff' || staffEnabled)
        .filter(item => item.id !== 'marketing' || marketingEnabled)
        .filter(item => item.id !== 'payments' || paymentsEnabled)
        .filter(item => item.id !== 'insights' || insightsEnabled)
        .filter(item => item.id !== 'services' || servicesEnabled)
        .filter(item => item.id !== 'products' || productsEnabled)
        .filter(item => !item.exclusiveRoleIds || item.exclusiveRoleIds?.includes(user?.role.id || -1))
        .map(item => {
            // Filter childs for services menu
            if (item.childs && item.id === 'services') {
                return {
                    ...item,
                    childs: item.childs.filter(child => {
                        if (child.id === 'daycare') return daycareEnabled;
                        if (child.id === 'boarding') return boardingEnabled;
                        if (child.id === 'transportation') return transportationEnabled;
                        if (child.id === 'discounts') return discountsEnabled;
                        if (child.id === 'memberships') return membershipsEnabled;
                        if (child.id === 'evaluation') return evaluationEnabled;
                        return true;
                    })
                };
            }

            if (item.childs && item.id === 'clients') {
                return {
                    ...item,
                    childs: item.childs.filter(child => {
                        if (child.id === 'agreements') return permissions.clients.agreements;
                        if (child.id === 'sold-memberships') return permissions.memberships.viewSold;
                        return true;
                    })
                };
            }
            return item;
        });
};
